import type { Match } from '@uefadigital/panenka';
import { PkBadge, PkButton, PkIcon, PkIdentifier, PkJersey } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { CSSProperties } from 'styled-components';

import { useGetLineUpQuery, useGetMatchEventsQuery, useGetPlayerStatusQuery } from 'api/fspApi';
import { routes } from 'routes';
import type { LineUpPlayer, LineUpTeam, LineUps, MatchEvents, Players, SquadPlayer } from 'types/ApiResponseInterfaces';
import type { ConfigCompetition } from 'utils/config';
import { getPlayerLink, handleGoToLink, isFemale } from 'utils/helpers';
import { translateEntity } from 'utils/translateEntity';

import * as style from './style.module.css';

const GOALKEEPER = 'GOALKEEPER';

interface IconsData {
  [key: string]: {
    name: string;
    color?: string;
  };
}

export interface SubstitutesData {
  matchData: Match;
  isDesktop: boolean;
  competition?: ConfigCompetition;
  noPosition?: boolean;
}

enum MatchEventNames {
  YELLOW_CARD = 'YELLOW_CARD',
  SECOND_YELLOW_CARD = 'SECOND_YELLOW_CARD',
  GOAL = 'GOAL',
  OWN_GOAL = 'OWN_GOAL',
  SUBSTITUTION = 'SUBSTITUTION',
  SUBSTITUTION_BACK = 'SUBSTITUTION_BACK',
  RED_CARD = 'RED_CARD',
}

enum PlayersStatus {
  SUSPENDED = 'SUSPENDED',
}

const cssPropertiesPrimary: CSSProperties = {
  fontWeight: 'var(--pk-font-weight-m)',
  fontFamily: 'var(--pk-font-base-regular)',
  lineHeight: 'var(--pk-line-height--sm)',
};

export const Substitutes = ({ matchData, isDesktop, competition, noPosition }: SubstitutesData) => {
  const navigate = useNavigate();

  const { t } = useTranslation();

  const {
    data: lineUps = {} as LineUps,
    isLoading: lineUpsLoading,
    isError: lineUpsError,
  } = useGetLineUpQuery(matchData.id);

  const { awayTeam, homeTeam } = lineUps;

  const { data: matchEvents = [], isLoading: matchEventsLoadings } = useGetMatchEventsQuery({
    matchId: matchData.id,
    filter: 'LINEUP',
    offset: 0,
    limit: 100,
  });

  const { data: homePlayerNotAvailable, isLoading: homePlayerNotAvailableLoading } = useGetPlayerStatusQuery({
    seasonYear: matchData.seasonYear,
    matchId: matchData.id,
    teamId: matchData.homeTeam?.id || '',
    status: PlayersStatus.SUSPENDED,
  });

  const { data: awayPlayerNotAvailable, isLoading: awayPlayerNotAvailableLoading } = useGetPlayerStatusQuery({
    seasonYear: matchData.seasonYear,
    matchId: matchData.id,
    teamId: matchData.awayTeam?.id || '',
    status: PlayersStatus.SUSPENDED,
  });

  if (
    lineUpsLoading ||
    lineUpsError ||
    matchEventsLoadings ||
    awayPlayerNotAvailableLoading ||
    homePlayerNotAvailableLoading
  ) {
    return <div />;
  }

  const playersToEvents: Record<string, MatchEvents[]> = [...awayTeam.bench, ...homeTeam.bench].reduce(
    (acc: Record<string, MatchEvents[]>, player) => {
      acc[player?.player?.id] = matchEvents.filter((event) => {
        return (
          event.primaryActor?.person?.id === player?.player?.id ||
          event.secondaryActor?.person?.id === player?.player?.id
        );
      });

      return acc;
    },
    {},
  );

  const renderGoal = (playerId: string) => {
    const playerGoal = playersToEvents[playerId].filter(
      (e) => e.type === MatchEventNames.GOAL && e?.subType !== 'OWN_GOAL',
    );

    return (
      !!playerGoal.length && (
        <Fragment>
          <PkIcon
            className="pr-icon-size--13"
            size={15}
            name={eventIcons[MatchEventNames.GOAL]?.name}
            color={eventIcons[MatchEventNames.GOAL]?.color}
          />
          {playerGoal
            .sort((goal, nextGoal) => {
              return new Date(goal.timestamp).getTime() - new Date(nextGoal.timestamp).getTime();
            })
            .map((goal, i) => {
              return (
                <span className="pr-font-size--10" key={goal.time.minute + i}>{`${goal.time.minute}${
                  goal.time?.injuryMinute ? `+${goal.time.injuryMinute}` : ''
                }' `}</span>
              );
            })}
        </Fragment>
      )
    );
  };

  const renderOwnGoal = (playerId: string) => {
    const playerOwnGoal = playersToEvents[playerId].filter(
      (e) => e.type === MatchEventNames.GOAL && e?.subType === 'OWN_GOAL',
    );

    return (
      !!playerOwnGoal.length && (
        <Fragment>
          <PkIcon
            className="pr-icon-size--13"
            size={15}
            name={eventIcons[MatchEventNames.OWN_GOAL]?.name}
            color={eventIcons[MatchEventNames.OWN_GOAL]?.color}
          />
          {playerOwnGoal
            .sort((goal, nextGoal) => {
              return new Date(goal.timestamp).getTime() - new Date(nextGoal.timestamp).getTime();
            })
            .map((goal, i: number) => {
              return (
                <span className="pr-font-size--10" key={goal.time.minute + i}>{`${goal.time.minute}${
                  goal.time?.injuryMinute ? `+${goal.time.injuryMinute}` : ''
                }' `}</span>
              );
            })}
        </Fragment>
      )
    );
  };

  const renderYellowCards = (playerId: string) => {
    const playerYellowCards = playersToEvents[playerId].filter((e) => e.type === MatchEventNames.YELLOW_CARD);

    const iconName =
      playerYellowCards.length === 1
        ? eventIcons[MatchEventNames.YELLOW_CARD]?.name
        : eventIcons[MatchEventNames.SECOND_YELLOW_CARD]?.name;

    const iconColor =
      playerYellowCards.length === 1
        ? eventIcons[MatchEventNames.YELLOW_CARD]?.color
        : eventIcons[MatchEventNames.SECOND_YELLOW_CARD]?.color;

    return (
      !!playerYellowCards.length && (
        <Fragment>
          <PkIcon className="pr-icon-size--13" size={15} name={iconName} color={iconColor} />
        </Fragment>
      )
    );
  };

  const renderRedCard = (playerId: string) => {
    const playerRedCard = playersToEvents[playerId].filter((e) => e.type === MatchEventNames.RED_CARD);

    const iconName = eventIcons[MatchEventNames.RED_CARD]?.name;
    const iconColor = eventIcons[MatchEventNames.RED_CARD]?.color;

    return (
      !!playerRedCard.length && (
        <Fragment>
          <PkIcon className="pr-icon-size--13" size={15} name={iconName} color={iconColor} />
        </Fragment>
      )
    );
  };

  const renderSubstitution = (playerId: string) => {
    const playerSubstitution = playersToEvents[playerId].filter((e) => e.type === MatchEventNames.SUBSTITUTION);

    const iconName =
      playerSubstitution.length === 1
        ? eventIcons[MatchEventNames.SUBSTITUTION]?.name
        : eventIcons[MatchEventNames.SUBSTITUTION_BACK]?.name;

    const iconColor =
      playerSubstitution.length === 1
        ? eventIcons[MatchEventNames.SUBSTITUTION]?.color
        : eventIcons[MatchEventNames.SUBSTITUTION_BACK]?.color;

    const substitutionTime =
      playerSubstitution.length > 1 ? (
        <Fragment>
          <span className="pr-font-size--10">&nbsp; in {playerSubstitution[1].time.minute}&apos;&nbsp;</span>
          <span className="pr-font-size--10">out {playerSubstitution[0].time.minute}&apos;</span>
        </Fragment>
      ) : (
        <span className="pr-font-size--10">{playerSubstitution[0]?.time?.minute}&apos;</span>
      );

    return (
      !!playerSubstitution.length && (
        <Fragment>
          <PkIcon className="pr-icon-size--13" size={15} name={iconName} color={iconColor} />
          {substitutionTime}
        </Fragment>
      )
    );
  };

  const displayPlayerName = (player: LineUpPlayer | SquadPlayer) => {
    if (translateEntity(player.player)?.shortName) {
      return translateEntity(player.player).shortName;
    }

    if ('jerseyNumber' in player) {
      return `${t('player')} ${player.jerseyNumber}`;
    }
    return t('player');
  };

  const eventIcons: IconsData = {
    SUBSTITUTION: { name: 'football-substitution-in', color: 'support-01A' },
    SUBSTITUTION_BACK: { name: 'football-substitution-both-color' },
    YELLOW_CARD: { name: 'football-card-single-card', color: 'support-05A' },
    SECOND_YELLOW_CARD: { name: 'football-card-yellow-to-red-color' },
    RED_CARD: { name: 'football-card-single-card', color: 'support-04A' },
    GOAL: { name: 'football-goal' },
    OWN_GOAL: { name: 'football-goal', color: 'support-04A' },
  };

  const displayBenchPlayers = (team: LineUpTeam) => {
    return team.bench.map((player) => {
      const playerLink = competition && getPlayerLink(player.player.id, competition);

      return (
        <PkIdentifier
          key={player.player.id}
          onClick={() => handleGoToLink(playerLink)}
          className={clsx('pk-py--xs2', style.pointer)}
        >
          <span slot="prefix" className="pk-mr--s">
            <PkJersey shirtColor={team.shirtColor} number={player.jerseyNumber} size={25} />
          </span>
          <span
            slot="primary"
            style={cssPropertiesPrimary}
            className={`${
              isDesktop ? 'pk-font-size--l pk-line-height--md pr-font-size--12' : 'pk-font-size--m pk-line-height--sm'
            }`}
          >
            {displayPlayerName(player)} {player.type === GOALKEEPER ? `(${t('gk')})` : null}
          </span>
          <span slot="secondary">
            <div className="pk-d--flex pk-flex--wrap">
              {renderGoal(player.player.id)}
              {renderOwnGoal(player.player.id)}
              {renderYellowCards(player.player.id)}
              {renderRedCard(player.player.id)}
              {renderSubstitution(player.player.id)}
            </div>
          </span>
          ,
        </PkIdentifier>
      );
    });
  };

  const displayNotAvailablePlayers = (team: Players | undefined, shirtColor: string) => {
    return team?.players.map((player, i) => {
      return (
        <PkIdentifier key={i}>
          <span slot="prefix" className="pk-mr--s">
            <PkJersey shirtColor={shirtColor} number={0} size={25} />
          </span>
          <span
            slot="primary"
            style={cssPropertiesPrimary}
            className={`${
              isDesktop ? 'pk-font-size--l pk-line-height--md pr-font-size--12' : 'pk-font-size--m pk-line-height--sm'
            }`}
          >
            {displayPlayerName(player)}
          </span>
          <span slot="secondary">
            <div className="pk-d--flex pk-flex--wrap">
              <PkIcon size={15} name="football-suspended" color="support-04A" />
              <p className="pk-ml--xs2">{t('suspended')}</p>
            </div>
          </span>
          ,
        </PkIdentifier>
      );
    });
  };

  const seeFellSquadRedirect = () => {
    const lastIndex = location.pathname.lastIndexOf('/');
    const redirectLink = location.pathname.substring(0, lastIndex).replace(/\/{2,}/g, '/');

    return navigate(`${redirectLink}/${routes.squadDetails}`);
  };

  const Title = isDesktop
    ? 'pk-font-size--xl2 pk-line-height--sd pk-font-style--normal pk-text--text-01 pr-font-size--16 pr-pb--12 pk-font--comp-bold'
    : 'pk-font-size--xl pk-line-height--sd pk-font-style--normal pk-text--text-01 pk-mb--m pk-font--comp-bold';

  const SubTitle = isDesktop
    ? 'pk-font-size--xl pk-line-height--sd pk-font-style--normal pk-text--text-01 pk-font--base-bold'
    : 'pk-font-size--m pk-line-height--sd pk-font-style--normal pk-text--text-01 pk-my--m pk-font--base-bold';

  return (
    <div className="pk-d--flex pk-flex--column">
      {!noPosition && <p className={Title}>{t(`players${isFemale(competition?.id) ? '_f' : ''}`)}</p>}
      {!noPosition && (
        <div className="pk-d--flex pk-justify-content-sm--between pk-pt-md--m pk-mb--xs pr-pt--0">
          <div className={clsx('pk-ml--auto pk-ml-md--0', style.flexOne)}>
            <PkIdentifier className="pr-pt--0">
              <span slot="prefix">
                <PkBadge
                  alt={homeTeam.team.internationalName ?? ''}
                  size={isDesktop ? 56 : 24}
                  fallbackImage="club-generic-badge"
                  src={homeTeam.team.logoUrl}
                  className="pr-badge-size--30"
                />
              </span>
              <span
                slot="primary"
                className={`${
                  isDesktop
                    ? 'pk-font-size--xl pk-line-height--md pr-font-size--14'
                    : 'pk-font-size--m pk-line-height--sm'
                } `}
              >
                {translateEntity(homeTeam?.team)?.displayName}
              </span>
            </PkIdentifier>
          </div>
          <div className={style.flexOne}>
            <PkIdentifier className="pk-mb--m pr-pt--0 pr-pb--2">
              <span slot="prefix">
                <PkBadge
                  alt={awayTeam.team.internationalName ?? ''}
                  size={isDesktop ? 56 : 24}
                  fallbackImage="club-generic-badge"
                  src={awayTeam.team.logoUrl}
                  className="pr-badge-size--30"
                />
              </span>
              <span
                slot="primary"
                className={`${
                  isDesktop
                    ? 'pk-font-size--xl pk-line-height--md pr-font-size--14'
                    : 'pk-font-size--m pk-line-height--sm'
                } `}
              >
                {translateEntity(awayTeam?.team)?.displayName}
              </span>
            </PkIdentifier>
          </div>
        </div>
      )}
      <p className={SubTitle}>{t(`substitutes${isFemale(competition?.id) ? '_f' : ''}`)}</p>
      <div className="pk-d--flex pk-justify-content-sm--between pk-pt-md--m pk-mb-md--xs">
        <div className={clsx('pk-overflow--hidden', style.flexOne)}>{displayBenchPlayers(homeTeam)}</div>
        <div className={clsx('pk-overflow--hidden', style.flexOne)}>{displayBenchPlayers(awayTeam)}</div>
      </div>
      {homePlayerNotAvailable?.players.length || awayPlayerNotAvailable?.players.length ? (
        <div>
          <p className={`${SubTitle} pk-pt-md--m`}>{t('not_available_players')}</p>
          <div className="pk-d--flex pk-justify-content-sm--between pk-pt-md--m pk-mb-md--xs">
            <div className={clsx('pk-overflow--hidden', style.flexOne)}>
              {displayNotAvailablePlayers(homePlayerNotAvailable, homeTeam.shirtColor)}
            </div>
            <div className={clsx('pk-overflow--hidden', style.flexOne)}>
              {displayNotAvailablePlayers(awayPlayerNotAvailable, awayTeam.shirtColor)}
            </div>
          </div>
        </div>
      ) : (
        ''
      )}
      {!noPosition && (
        <div className={`${isDesktop ? '' : 'pk-align-self--start'}  pk-pt-sm--l pr-display--none`}>
          <PkButton
            appearance="text"
            title={t('see_full_squad_details')}
            aria-label={t('see_full_squad_details')}
            onClick={seeFellSquadRedirect}
          >
            <span className=" pk-line-height--md">{t('see_full_squad_details')}</span>
            <PkIcon name="navigation-chevron-right" />
          </PkButton>
        </div>
      )}
    </div>
  );
};
